import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Allblogs.css";

const BlogHero = () => (
  <div className="blog-header">
    <div className="header-content">
      <h1>Landscaping Tips and Insights</h1>
      <div className="blog-title-underline"></div>
      <p>Practical guidance for healthier lawns and better outdoor spaces</p>
    </div>
  </div>
);

const Allblogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/blogs`
        );
        if (!res.ok) throw new Error(`Failed to fetch (HTTP ${res.status})`);
        const data = await res.json();
        setBlogs(data);
      } catch (err) {
        setError("We couldn't load the content. Please try again later.");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const filteredBlogs = blogs
    .filter((blog) => {
      const title = (blog.title || "").toLowerCase();
      const contents = (blog.contents || "").toLowerCase();
      const q = searchQuery.toLowerCase();

      const matchesSearch = title.includes(q) || contents.includes(q);
      const matchesCategory =
        categoryFilter === "all" || blog.category === categoryFilter;

      return matchesSearch && matchesCategory;
    })
    // default: newest first
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const categories = ["all", ...new Set(blogs.map((blog) => blog.category))];

  if (loading) {
    return (
      <div className="all-blogs-container">
        <BlogHero />
        <div className="loading-state">
          <div className="spinner-container"><div className="spinner"></div></div>
          <p>Loading articles...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="all-blogs-container">
        <BlogHero />
        <div className="error-state">
          <div className="error-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2>Content Unavailable</h2>
          <p>{error}</p>
          <button className="retry-button" onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="all-blogs-container">
      <BlogHero />

      <div className="blog-content-wrapper">
        <div className="blog-controls">
          <div className="search-container">
            <div className="search-bar">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>

              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              {searchQuery && (
                <button
                  className="clear-search"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="filter-container">
            <div className="filter-group">
              <div className="select-wrapper">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="category-filter"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category === "all" ? "All Categories" : category}
                    </option>
                  ))}
                </select>

                <svg className="select-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="results-info">
          <h2>
            {categoryFilter === "all" ? "Latest Articles" : `${categoryFilter} Articles`}
            {searchQuery && ` matching "${searchQuery}"`}
          </h2>

          <span className="results-count">
            {filteredBlogs.length} {filteredBlogs.length === 1 ? "article" : "articles"} found
          </span>
        </div>

        {filteredBlogs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3>No Matching Articles</h3>
            <p>We couldn't find any articles matching your criteria.</p>
            <button
              className="reset-filters"
              onClick={() => {
                setSearchQuery("");
                setCategoryFilter("all");
              }}
            >
              Reset Filters
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        ) : (
          <div className="blog-grid">
            {filteredBlogs.map((post) => (
              <article
                key={post.id}
                className="blog-card"
              >
                <div className="card-image-container">
                  <div className="image-overlay"></div>

                  <img
                    src={post.img || ""}
                    alt={post.title}
                    className="card-image"
                    loading="lazy"
                  />

                  <span className="category-badge">{post.category}</span>

                  <div className="card-date">
                    {new Date(post.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                </div>

                <div className="card-content">
                  <div className="card-meta">
                    <span className="read-time">{post.readTime || "5 min"} read</span>
                    <span className="author">by {post.author || "Unknown Author"}</span>
                  </div>

                  <h3>{post.title}</h3>

                  <p className="card-excerpt">{post.contents?.slice(0, 120)}…</p>

                  <Link to={`/blogs/${post.id}`} className="read-more-button" aria-label={`Read ${post.title}`}>
                    Read More
                    <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Allblogs;
